import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LoadingSpinner, Button } from '@airclou/ui'
import { HTTP_TIMEOUT_MS, SUPPORT_EMAIL } from '../config'
import { CheckCircle, XCircle, AlertTriangle, ArrowRight } from 'lucide-react'

type AcceptanceState = 'loading' | 'success' | 'error'

interface AcceptanceResult {
  company_name: string
  role: string
}

interface AcceptanceError {
  code: string
  message: string
}

export function AcceptInvitationPage() {
  const { t } = useTranslation('dashboard')
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<AcceptanceState>('loading')
  const [result, setResult] = useState<AcceptanceResult | null>(null)
  const [error, setError] = useState<AcceptanceError | null>(null)

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setState('error')
      setError({
        code: 'MISSING_TOKEN',
        message: t('invitation.missingToken')
      })
      return
    }

    acceptInvitation(token)
  }, [token])

  const acceptInvitation = async (invitationToken: string) => {
    setState('loading')
    
    try {
      const response = await fetch('/api/company-invitations/accept', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
        body: JSON.stringify({ token: invitationToken })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        setState('error')
        const errorCode = data.error?.code || data.code || 'UNKNOWN_ERROR'
        setError({
          code: errorCode,
          message: getErrorMessage(errorCode)
        })
        return
      }

      setState('success')
      setResult(data.data)
    } catch (err) {
      setState('error')
      setError({
        code: 'NETWORK_ERROR',
        message: t('invitation.networkError')
      })
    }
  }

  const getErrorMessage = (code: string): string => {
    switch (code) {
      case 'INVALID_TOKEN':
        return t('invitation.invalidToken')
      case 'INVITATION_EXPIRED':
        return t('invitation.invitationExpired')
      case 'INVITATION_ALREADY_ACCEPTED':
        return t('invitation.invitationAlreadyAccepted')
      case 'INVITATION_CANCELLED':
        return t('invitation.invitationCancelled')
      case 'AUTH_SERVICE_ERROR':
        return t('invitation.authServiceError')
      default:
        return t('invitation.unknownError')
    }
  }

  const handleContinue = () => {
    // Redirect to auth login which will then redirect back to dashboard
    const authUrl = import.meta.env.VITE_AUTH_URL || 'https://auth.airclou.com'
    const dashboardUrl = window.location.origin
    window.location.href = `${authUrl}/login?app=dashboard&redirect=${encodeURIComponent(dashboardUrl)}`
  }

  const handleRetry = () => {
    if (token) {
      acceptInvitation(token)
    }
  }

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-primary-foreground font-bold text-xl rounded-lg mb-4">
            D
          </div>
          <h1 className="text-2xl font-semibold text-foreground">{t('invitation.dashboard')}</h1>
        </div>

        {/* Card */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          {state === 'loading' && (
            <div className="text-center py-8">
              <LoadingSpinner
                text={t('invitation.acceptingInvitation')}
                textPosition="bottom"
              />
            </div>
          )}

          {state === 'success' && result && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {t('invitation.welcomeTo', { name: result.company_name })}
              </h2>
              <p className="text-muted-foreground mb-6">
                {t('invitation.acceptedRole')}{' '}
                <span className="font-medium">{result.role}</span>.
              </p>
              <Button 
                onClick={handleContinue}
                className="w-full"
              >
                {t('invitation.continueToDashboard')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {state === 'error' && error && (
            <div className="text-center">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                error.code === 'INVITATION_ALREADY_ACCEPTED' 
                  ? 'bg-primary/15' 
                  : 'bg-red-100 dark:bg-red-900/30'
              }`}>
                {error.code === 'INVITATION_ALREADY_ACCEPTED' ? (
                  <AlertTriangle className="w-8 h-8 text-primary" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                )}
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {error.code === 'INVITATION_ALREADY_ACCEPTED'
                  ? t('invitation.alreadyAccepted')
                  : t('invitation.unableToAccept')}
              </h2>
              <p className="text-muted-foreground mb-6">
                {error.message}
              </p>
              <div className="space-y-3">
                {error.code === 'INVITATION_ALREADY_ACCEPTED' && (
                  <Button 
                    onClick={handleContinue}
                    className="w-full"
                  >
                    {t('invitation.goToDashboard')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                {(error.code === 'NETWORK_ERROR' || error.code === 'AUTH_SERVICE_ERROR') && (
                  <Button 
                    onClick={handleRetry}
                    className="w-full"
                  >
                    {t('common.tryAgain')}
                  </Button>
                )}
                {error.code !== 'INVITATION_ALREADY_ACCEPTED' && (
                  <Button 
                    variant="outline"
                    onClick={handleContinue}
                    className="w-full"
                  >
                    {t('invitation.goToLogin')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('invitation.needHelp')}{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
            {t('invitation.contactSupport')}
          </a>
        </p>
      </div>
    </div>
  )
}
